import { useEffect, useState } from 'react';
import type { WizardPageComponent } from '../../config/wizardPagesConfig';
import { resumeOnsaSelector } from '../../recoil/selectors/resumeOnsaSelector';
import {
    useRecoilCallback,
    useRecoilState,
    useRecoilValue,
    useResetRecoilState,
    useSetRecoilState
} from 'recoil';
import useWizardNavigation from '../../hooks/useWizardNavigation';
import { accountHolderAtom } from '../../recoil/atoms/accountHolderAtom';
import { jobInfoAtom } from '../../recoil/atoms/jobInfoAtom';
import {
    type AllCoverables,
    type ProductStatus,
    type OnsaStep
} from '../../recoil/types/onsaTypes';
import CHAModal from '../../shared_components/CHAModal/CHAModal';
import MergeBasketModal from '../../shared_components/MergeBasketModal/MergeBasketModal';
import {
    allCoverablesOnsaToCoverableDTOArray,
    allCoverablesToCoverableDTOArray,
    getCurrentProduct,
    shouldImmediatelyWithdrawJobForProduct
} from '../../utils/coverablesUtil';
import { onsaInfoAtom } from '../../recoil/atoms/onsaInfoAtom';
import { allCoverablesOnsaAtom } from '../../recoil/atoms/allCoverablesOnsaAtom';
import { onsaProductStatusListAtom } from '../../recoil/atoms/onsaProductStatusListAtom';
import { LoadingDots } from '@flos/react-ui';
import CustomerStopModal from '../../shared_components/CustomerStopModal/CustomerStopModal';
import { useCheckCoverableForDoubleInsurance } from '../../recoil/hooks/useCheckCoverableForDoubleInsurance';
import {
    type CoverableDTO,
    type ConflictingCoverableDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { getOnsaCurrentProductSelector } from '../../recoil/selectors/getOnsaCurrentProductSelector';
import RemoveModal from '../../shared_components/RemoveModal/RemoveModal';
import ViewExistingInsuranceModal from '../../shared_components/ViewExistingInsuranceModal/ViewExistingInsuranceModal';
import { getPrimaryAccountNumber } from '../../utils/topConfigContextUtil';
import { topConfigContextAtom } from '../../recoil/atoms/topConfigContextAtom';
import { allCoverablesAtom } from '../../recoil/atoms/allCoverablesAtom';
import { refreshCoverablesSelectorAtom } from '../../recoil/atoms/refreshCoverablesSelectorAtom';
import _ from 'lodash';
import { refreshCoverablesOnsaSelectorAtom } from '../../recoil/atoms/refreshCoverablesOnsaSelectorAtom';
import { useWithdrawJob } from '../../recoil/hooks/useWithdrawJob';
import { jobAtom } from '../../recoil/atoms/jobAtom';
import { WITHDRAWN } from '../../recoil/types/types';
import { sortCoverablesByFlavourName } from '../../utils/sortingUtil';
import type { YoungAccidentOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/accident';
import { calculateAge } from '../../utils/dateUtil';
import { isAgeTooOldForFlavour } from '../../utils/validationUtil';
import { useHippoText } from '../../hooks/useHippoText';
import { refreshJobDTOAtom } from '../../recoil/atoms/refreshJobDTOAtom';
import { refreshCustomerBenefitsAtom } from '../../recoil/atoms/refreshCustomerBenefitsAtom';
import { refreshIsGiftCardEligibleSelectorAtom } from '../../recoil/atoms/refreshIsGiftCardEligibleSelectorAtom';

interface Conflict {
    coverable: CoverableDTO;
    conflictingCoverable: ConflictingCoverableDTO;
}

const ResumeOnsaPage: WizardPageComponent = () => {
    const getHippoText = useHippoText();
    const { wizardGotoStep } = useWizardNavigation();
    const { withdrawJob } = useWithdrawJob();
    const setJobDTO = useSetRecoilState(jobAtom);
    const refreshJobDTO = useSetRecoilState(refreshJobDTOAtom);
    const accountHolder = useRecoilValue(accountHolderAtom);
    const resetCoverables = useResetRecoilState(allCoverablesAtom);
    const refreshCoverablesSelector = useSetRecoilState(refreshCoverablesSelectorAtom);
    const refreshIsGiftCardEligible = useSetRecoilState(refreshIsGiftCardEligibleSelectorAtom);
    const refreshCoverablesOnsaSelector = useSetRecoilState(refreshCoverablesOnsaSelectorAtom);
    const setAllCoverablesOnsaAtom = useSetRecoilState(allCoverablesOnsaAtom);
    const refreshCustomerBenefits = useSetRecoilState(refreshCustomerBenefitsAtom);

    const [productStatusList, setProductStatusList] = useRecoilState(onsaProductStatusListAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);

    const topConfigContext = useRecoilValue(topConfigContextAtom);
    const resumeOnsaInfo = useRecoilValue(resumeOnsaSelector);

    const onsaInfo = useRecoilValue(onsaInfoAtom);
    const jobInfo = useRecoilValue(jobInfoAtom);
    const { checkCoverableForDoubleInsurance } = useCheckCoverableForDoubleInsurance();

    const [showCHAModal, setShowCHAModal] = useState<boolean | undefined>(undefined);
    const [showMergeAccountModal, setShowMergeAccountModal] = useState<boolean | undefined>(
        undefined
    );
    const [showAccRemoveModal, setShowAccRemoveModal] = useState<boolean | undefined>(undefined);
    const [showPrcRemoveModal, setShowPrcRemoveModal] = useState<boolean | undefined>(undefined);
    const [showConflictingRemoveModal, setShowConflictingRemoveModal] = useState<boolean>(false);
    const [showViewInsuranceModal, setShowViewInsuranceModal] = useState<boolean>(false);
    const [showStopModal, setShowStopModal] = useState<boolean>(false);

    const [conflicts, setConflicts] = useState<Conflict[] | undefined>(undefined);
    const [conflictingCoverable, setConflictingCoverable] = useState<ConflictingCoverableDTO>();
    const [coverableToRemove, setCoverableToRemove] = useState<CoverableDTO | undefined>(undefined);

    const onStopModalClose = (): void => {
        window.location.href = '/';
    };

    useEffect(() => {
        if (resumeOnsaInfo.stopCause !== 'None') {
            setShowStopModal(true);
        } else if (onsaInfo.onsaStep) {
            resetCoverables();
            refreshCoverablesSelector({});
            refreshCoverablesOnsaSelector({});
            refreshJobDTO({});
            void handleModalsAndRedirect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateProductStatusList = (response: AllCoverables): Record<string, ProductStatus[]> => {
        const updatedList: Record<string, ProductStatus[]> = { ...productStatusList };

        for (const property in response) {
            const coverables = response[property as keyof AllCoverables];

            if (Array.isArray(coverables) && coverables.length > 0) {
                updatedList[property] = [...coverables]
                    .sort(
                        (a, b) =>
                            (Object.entries(b).length - Object.entries(a).length ||
                                a.fixedId?.localeCompare(b.fixedId ?? '')) ??
                            0
                    )
                    .map((coverable) => ({
                        calculated: false,
                        fixedId: coverable.fixedId ?? '',
                        flavourName: coverable.flavourName ?? '',
                        lobCode: coverable.lobCode ?? '',
                        flavourCode: coverable.flavourCode ?? ''
                    }));
            }
        }
        return updatedList;
    };

    const handleRemoveAndContinue = useRecoilCallback(
        ({ snapshot }) =>
            async (foundConflicts: Conflict[]) => {
                if (foundConflicts.length > 0) {
                    const updatedOnsaCoverables = await snapshot.getPromise(allCoverablesOnsaAtom);
                    const updatedShoppingBasketCoverables =
                        await snapshot.getPromise(allCoverablesAtom);
                    const onsaCoverablesList = allCoverablesOnsaToCoverableDTOArray(
                        updatedOnsaCoverables
                    ).sort(sortCoverablesByFlavourName);
                    const shoppingBasketCoverablesList = allCoverablesToCoverableDTOArray(
                        updatedShoppingBasketCoverables
                    );
                    const { coverable, conflictingCoverable } = foundConflicts[0];
                    setConflictingCoverable(conflictingCoverable);
                    setCoverableToRemove(coverable);
                    if (onsaCoverablesList.length + shoppingBasketCoverablesList.length === 1) {
                        if (shouldImmediatelyWithdrawJobForProduct(coverable.flavourName ?? '')) {
                            void withdrawJob().then(() => {
                                setJobDTO((jobDTO) => {
                                    return { ...jobDTO, jobStatus: WITHDRAWN };
                                });
                            });
                        }
                        setShowViewInsuranceModal(true);
                    } else {
                        setShowConflictingRemoveModal(true);
                    }
                } else await redirectAfterResume();
            }
    );

    const handleAccidentAgeValidation = (onsaCoverablesList: CoverableDTO[]): boolean => {
        const covToRemove = onsaCoverablesList.find(
            (cov) =>
                (cov.flavourName === 'Ulykkesforsikring' ||
                    cov.flavourName === 'Ulykkesforsikring til unge') &&
                (cov as YoungAccidentOnsaDTO).firstName === accountHolder.firstName &&
                (cov as YoungAccidentOnsaDTO).lastName === accountHolder.lastName
        );

        if (!covToRemove?.flavourName) return false;

        const age = calculateAge(accountHolder.dateOfBirth ?? {}, 40);
        if (isAgeTooOldForFlavour(age, covToRemove?.flavourName)) {
            setCoverableToRemove(covToRemove);
            return true;
        } else {
            return false;
        }
    };

    const handleContentAgeValidation = (onsaCoverablesList: CoverableDTO[]): boolean => {
        const covToRemove = onsaCoverablesList.find(
            (cov) => cov.flavourName === 'Indboforsikring til unge'
        );
        if (!covToRemove?.flavourName) return false;

        const age = calculateAge(accountHolder.dateOfBirth ?? {}, 40);
        if (isAgeTooOldForFlavour(age, covToRemove?.flavourName)) {
            setCoverableToRemove(covToRemove);
            return true;
        } else {
            return false;
        }
    };

    const getConflictForCoverable = async (coverable: CoverableDTO): Promise<Conflict> => {
        const conflictingCov = await checkCoverableForDoubleInsurance(
            coverable?.lobCode ?? '',
            coverable?.fixedId ?? '',
            getPrimaryAccountNumber(topConfigContext)
        );
        return {
            coverable,
            conflictingCoverable: conflictingCov
        };
    };

    const getConflicts = async (onsaCoverablesList: CoverableDTO[]): Promise<Conflict[]> => {
        if (conflicts) {
            return conflicts;
        }
        const getConflictPromises = onsaCoverablesList.map(getConflictForCoverable);

        const foundConflicts = (await Promise.all(getConflictPromises)).filter(
            (conflict) => !_.isEmpty(conflict.conflictingCoverable)
        );

        setConflicts(foundConflicts);

        return foundConflicts;
    };

    const handleModalsAndRedirect = useRecoilCallback(({ snapshot }) => async () => {
        const updatedOnsaCoverables = await snapshot.getPromise(allCoverablesOnsaAtom);
        const onsaCoverablesList = allCoverablesOnsaToCoverableDTOArray(updatedOnsaCoverables).sort(
            sortCoverablesByFlavourName
        );

        if (
            onsaInfo.onsaStep !== 'ShoppingBasketPage' &&
            showAccRemoveModal === undefined &&
            showPrcRemoveModal === undefined
        ) {
            const updatedProductStatusList = updateProductStatusList(updatedOnsaCoverables);
            setProductStatusList(updatedProductStatusList);
        }

        if (jobInfo.isExistingBasket) {
            if (showMergeAccountModal === undefined) {
                setShowMergeAccountModal(true);
                return;
            }
        }

        if (showPrcRemoveModal === undefined && handleContentAgeValidation(onsaCoverablesList)) {
            setShowPrcRemoveModal(true);
            return;
        }

        if (showAccRemoveModal === undefined && handleAccidentAgeValidation(onsaCoverablesList)) {
            setShowAccRemoveModal(true);
            return;
        }

        const foundConflicts = await getConflicts(onsaCoverablesList);

        if (accountHolder.isExistingCustomer && foundConflicts.length === 0) {
            if (showCHAModal === undefined) {
                setShowCHAModal(true);
                return;
            }
        }

        if (foundConflicts.length === 0) await redirectAfterResume();
        else await handleRemoveAndContinue(foundConflicts);
    });

    const getNextPage = (productList: Record<string, ProductStatus[]>): OnsaStep => {
        if (
            onsaInfo.onsaStep === 'CoveragePackagePage' &&
            getCurrentProduct(productList)?.flavourName === 'Bilforsikring'
        ) {
            return 'PostLoginQuestionsPage';
        } else if (
            onsaInfo.onsaStep === 'CoveragePackagePage' ||
            onsaInfo.onsaStep === 'ShoppingBasketPage'
        ) {
            return onsaInfo.onsaStep;
        }
        return undefined;
    };

    const redirectAfterResume = useRecoilCallback(({ snapshot }) => async () => {
        const updatedProductStatusList = await snapshot.getPromise(onsaProductStatusListAtom);
        const hasUncalculatedProduct = Object.values(updatedProductStatusList).some((flavour) =>
            flavour
                .filter(
                    (cov) =>
                        cov.fixedId !== coverableToRemove?.fixedId ||
                        cov.flavourName !== coverableToRemove?.flavourName
                )
                .some((cov) => !cov.calculated)
        );
        const nextPage = getNextPage(updatedProductStatusList);

        refreshIsGiftCardEligible({});
        refreshCustomerBenefits({});
        if (
            (coverableToRemove === undefined ||
                currentProduct?.fixedId !== coverableToRemove?.fixedId ||
                currentProduct?.flavourName !== coverableToRemove?.flavourName) &&
            nextPage
        ) {
            if (nextPage === 'ShoppingBasketPage') {
                setAllCoverablesOnsaAtom({});
            }
            wizardGotoStep(nextPage);
        } else if (hasUncalculatedProduct) {
            wizardGotoStep('CoverableInformationPage');
        } else {
            setAllCoverablesOnsaAtom({});
            resetCoverables();
            refreshCoverablesSelector({});
            wizardGotoStep('ShoppingBasketPage');
        }
    });

    if (showCHAModal) {
        return (
            <CHAModal
                showModal={showCHAModal}
                closeModal={() => {
                    setShowCHAModal(false);
                    void handleModalsAndRedirect();
                }}
            />
        );
    }
    if (showMergeAccountModal) {
        return (
            <MergeBasketModal
                showModal={showMergeAccountModal}
                closeModal={() => {
                    setShowMergeAccountModal(false);
                    void handleModalsAndRedirect();
                }}
                nextPage={getNextPage(productStatusList)}
            />
        );
    }
    if (showStopModal) {
        return (
            <CustomerStopModal
                stopCause={resumeOnsaInfo.stopCause ?? 'None'}
                showModal={showStopModal}
                closeModal={onStopModalClose}
            />
        );
    }

    if (showPrcRemoveModal && coverableToRemove) {
        return (
            <RemoveModal
                coverable={coverableToRemove}
                title={getHippoText('DSS-REMOVE-CONTINUE-MODAL-OTHER-CONTENT-HEADER')}
                description={getHippoText('DSS-REMOVE-CONTINUE-MODAL-OTHER-CONTENT-SUBHEADER')}
                isRemoveAndContinueModal
                isCalledFromResumeOnsa
                showModal={showPrcRemoveModal}
                closeModal={() => {
                    setShowPrcRemoveModal(false);
                    void handleModalsAndRedirect();
                }}
            />
        );
    }

    if (showAccRemoveModal && coverableToRemove) {
        return (
            <RemoveModal
                coverable={coverableToRemove}
                title={
                    coverableToRemove?.flavourName === 'Ulykkesforsikring'
                        ? getHippoText('DSS-REMOVE-CONTINUE-MODAL-80-YEARS-TOO-OLD-HEADER')
                        : getHippoText('DSS-REMOVE-CONTINUE-MODAL-28-YEARS-TOO-OLD-HEADER')
                }
                description={
                    coverableToRemove?.flavourName === 'Ulykkesforsikring'
                        ? getHippoText('DSS-REMOVE-CONTINUE-MODAL-80-YEARS-TOO-OLD-SUBHEADER')
                        : getHippoText('DSS-REMOVE-CONTINUE-MODAL-28-YEARS-TOO-OLD-SUBHEADER')
                }
                isRemoveAndContinueModal
                isCalledFromResumeOnsa
                showModal={showAccRemoveModal}
                closeModal={() => {
                    setShowAccRemoveModal(false);
                    void handleModalsAndRedirect();
                }}
            />
        );
    }

    if (showConflictingRemoveModal && conflicts && coverableToRemove) {
        return (
            <RemoveModal
                coverable={coverableToRemove}
                isRemoveAndContinueModal
                isCalledFromResumeOnsa
                showModal={showConflictingRemoveModal}
                closeModal={() => {
                    setShowConflictingRemoveModal(false);
                    setConflicts((prev) => (prev ? prev.slice(1) : []));
                    void handleRemoveAndContinue(conflicts.slice(1));
                }}
            />
        );
    }

    if (showViewInsuranceModal && conflictingCoverable && coverableToRemove) {
        return (
            <ViewExistingInsuranceModal
                coverableToRemove={coverableToRemove}
                conflictingCoverable={conflictingCoverable}
                showModal={showViewInsuranceModal}
            />
        );
    }

    return <LoadingDots />;
};

export default ResumeOnsaPage;
